package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.ControlHeight
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.ui.ButtonWidthLock
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.Color as ComposeColor
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.dom.Span
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.dom.Button as ButtonElement

enum class ButtonVariant { Primary, Secondary, Ghost, Danger, DangerOutline, Link }

enum class ButtonSize { Sm, Md, Lg }

enum class ButtonType(internal val attribute: String) {
    Button("button"),
    Submit("submit"),
    Reset("reset"),
}

internal data class ButtonMetrics(
    val height: CSSSizeValue<CSSUnit.px>,
    val padX: CSSSizeValue<CSSUnit.px>,
    val fontSize: CSSSizeValue<CSSUnit.px>,
    val gap: CSSSizeValue<CSSUnit.px>,
)

internal fun ButtonSize.metricsFor(variant: ButtonVariant): ButtonMetrics {
    val base = when (this) {
        ButtonSize.Sm -> ButtonMetrics(ControlHeight.sm, Space.s2, Typography.bodySmall.size, Space.s1)
        ButtonSize.Md -> ButtonMetrics(ControlHeight.md, Space.s3, Typography.label.size, Space.s2)
        ButtonSize.Lg -> ButtonMetrics(ControlHeight.lg, Space.s4, Typography.body.size, Space.s2)
    }
    return if (variant == ButtonVariant.Link) base.copy(padX = Space.s0) else base
}

internal fun ButtonVariant.className(): String = when (this) {
    ButtonVariant.Primary -> ControlClass.BUTTON_PRIMARY
    ButtonVariant.Secondary -> ControlClass.BUTTON_SECONDARY
    ButtonVariant.Ghost -> ControlClass.BUTTON_GHOST
    ButtonVariant.Danger -> ControlClass.BUTTON_DANGER
    ButtonVariant.DangerOutline -> ControlClass.BUTTON_DANGER_OUTLINE
    ButtonVariant.Link -> ControlClass.BUTTON_LINK
}

internal fun buttonLook(variant: ButtonVariant, size: ButtonSize, fullWidth: Boolean, interactive: Boolean): Modifier {
    val metrics = size.metricsFor(variant)
    val base = Modifier
        .classNames(ControlClass.BUTTON, variant.className())
        .height(metrics.height)
        .padding(topBottom = Space.s0, leftRight = metrics.padX)
        .fontSize(metrics.fontSize)
        .styleModifier {
            property("gap", "${metrics.gap}")
            if (fullWidth) property("width", "100%")
        }
    return if (interactive) base.pressable() else base
}

internal fun buttonSpinnerColor(variant: ButtonVariant): CSSColorValue = when (variant) {
    ButtonVariant.Primary -> Palette.onPrimary
    ButtonVariant.Danger -> Palette.status(StatusHue.Danger).onSolid
    ButtonVariant.Secondary, ButtonVariant.Ghost, ButtonVariant.Link -> Palette.textSecondary
    ButtonVariant.DangerOutline -> Palette.status(StatusHue.Danger).solid
}

private val SPINNER_PX = IconSize.sm.value.toInt()

internal fun buttonSpinnerTrack(variant: ButtonVariant): CSSColorValue = when (variant) {
    ButtonVariant.Primary, ButtonVariant.Danger -> ComposeColor("transparent")
    ButtonVariant.Secondary, ButtonVariant.Ghost, ButtonVariant.Link, ButtonVariant.DangerOutline -> Palette.borderSubtle
}

@Composable
fun Button(
    label: String,
    variant: ButtonVariant = ButtonVariant.Primary,
    size: ButtonSize = ButtonSize.Md,
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
    loading: Boolean = false,
    leadingIcon: (@Composable () -> Unit)? = null,
    trailingIcon: (@Composable () -> Unit)? = null,
    fullWidth: Boolean = false,
    type: ButtonType = ButtonType.Button,
    onClick: () -> Unit,
) {
    ButtonFrame(
        variant = variant,
        size = size,
        enabled = enabled,
        modifier = modifier,
        onClick = onClick,
        loading = loading,
        fullWidth = fullWidth,
        type = type,
    ) {
        ButtonContent(
            label = label,
            variant = variant,
            loading = loading,
            leadingIcon = leadingIcon,
            trailingIcon = trailingIcon,
        )
    }
}

private class WidthLockHolder {
    var element: HTMLElement? = null
    var idleWidthPx: Double? = null
    var lockPx: Int? = null
}

@Composable
internal fun ButtonFrame(
    variant: ButtonVariant,
    size: ButtonSize,
    enabled: Boolean,
    modifier: Modifier,
    onClick: () -> Unit,
    loading: Boolean = false,
    fullWidth: Boolean = false,
    type: ButtonType = ButtonType.Button,
    content: @Composable () -> Unit,
) {
    val interactive = enabled && !loading
    val holder = remember { WidthLockHolder() }
    holder.lockPx = ButtonWidthLock.resolve(loading && !fullWidth, holder.idleWidthPx, holder.lockPx)
    SideEffect {
        if (!loading) holder.idleWidthPx = holder.element?.offsetWidth?.toDouble()
    }
    val lockedWidth = holder.lockPx

    ButtonElement(
        attrs = buttonLook(variant, size, fullWidth, interactive)
            .styleModifier {
                if (lockedWidth != null) property("width", "${lockedWidth}px")
            }
            .onClick { event ->
                if (interactive) {
                    holder.idleWidthPx = holder.element?.offsetWidth?.toDouble() ?: holder.idleWidthPx
                    onClick()
                } else {
                    event.preventDefault()
                }
            }
            .onMouseDown { event -> if (!enabled) event.preventDefault() }
            .then(modifier)
            .toAttrs {
                attr("type", type.attribute)
                if (!enabled) attr("aria-disabled", "true")
                if (loading) attr("aria-busy", "true")
                ref { element ->
                    holder.element = element
                    onDispose { if (holder.element === element) holder.element = null }
                }
            },
    ) {
        content()
    }
}

@Composable
private fun ButtonContent(
    label: String,
    variant: ButtonVariant,
    loading: Boolean,
    leadingIcon: (@Composable () -> Unit)?,
    trailingIcon: (@Composable () -> Unit)?,
) {
    when {
        loading -> IconSlot {
            Spinner(
                sizePx = SPINNER_PX,
                color = buttonSpinnerColor(variant),
                ariaLabel = "",
                trackColor = buttonSpinnerTrack(variant),
            )
        }
        leadingIcon != null -> IconSlot(leadingIcon)
    }
    SpanText(text = label)
    if (trailingIcon != null) IconSlot(trailingIcon)
}

@Composable
internal fun IconSlot(content: @Composable () -> Unit) {
    Span(
        attrs = Modifier
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("flex-shrink", "0")
            }
            .toAttrs { attr("aria-hidden", "true") },
    ) { content() }
}
